import { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { COMMERCE_BACKEND_CCP } from '@atlassian/commerce-ui/rollout';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import type { BillingSourceSystem } from '../graphql/__types__/TrialDetailsQuery.ts';
import type { CommerceBackendName } from '../useCommerceBackendName.ts';

export const useIsEligibleForPremiumReverseTrialsExperiment = ({
	isReverseTrialer,
	edition,
	isInTrial = false,
	isInPredunning = false,
	hasBillingInformation,
	commerceBackendName,
	isPlanSelectionPageUrlAvailable,
}: {
	isReverseTrialer: boolean;
	edition: ConfluenceEdition | null;
	isInTrial?: boolean;
	isInPredunning?: boolean;
	hasBillingInformation: boolean;
	commerceBackendName: BillingSourceSystem | CommerceBackendName | null;
	isPlanSelectionPageUrlAvailable?: boolean | null;
}) =>
	Boolean(
		edition === ConfluenceEdition.PREMIUM &&
		(isInTrial || isInPredunning) &&
		isReverseTrialer &&
		hasBillingInformation === false &&
		commerceBackendName === COMMERCE_BACKEND_CCP &&
		isPlanSelectionPageUrlAvailable,
	);

export const usePremiumReverseTrialABCohort = ({ isEligible, touchpointId, attributes }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [premiumReverseTrialABCohort, setPremiumReverseTrialABCohort] = useState<
		string | undefined
	>(undefined);

	useEffect(() => {
		if (isEligible && fg('growth_confluence_buy_reverse_trials_fg')) {
			setPremiumReverseTrialABCohort(
				expVal('growth_confluence_buy_reverse_trials_ab', 'cohort', 'not-enrolled'),
			);
		} else if (!premiumReverseTrialABCohort) {
			setPremiumReverseTrialABCohort('not-enrolled');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEligible]);

	useEffect(() => {
		if (premiumReverseTrialABCohort === 'control' || premiumReverseTrialABCohort === 'variation') {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: 'growth_confluence_buy_reverse_trials_ab-exposed',
					attributes: {
						cohort: premiumReverseTrialABCohort,
						touchpointId,
						...attributes,
					},
					source: 'growth_confluence_buy_reverse_trials_ab',
				},
			}).fire();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [premiumReverseTrialABCohort]);

	return premiumReverseTrialABCohort;
};
